import React, { useEffect, useState } from 'react'

import CampaignPage from 'Containers/Campaigns/Page'
import { currencyFormatter } from 'Utils/Formatters'
import useGraphQLFetch from 'Utils/Hooks/useGraphQLFetch'
import { mockRosters } from 'Utils/Mission'
import { apiClient } from 'Module/API/Client'

import SubmitButton from 'Components/Form/SubmitButton'
import { PageWrapper } from 'Components/Campaigns/Page/PageWrapper'
import { LayoutDashboard } from 'Components/Layout/Dashboard'
import { Table } from 'Components/Table'
import { Td } from 'Components/Table/Td'
import { Th } from 'Components/Table/Th'
import { Tr } from 'Components/Table/Tr'

import {
  getWeekNumberAndYear,
  dropTimeZoneFromDate,
  getDateOfISOWeek,
  formatToLocalDate,
  padZero,
} from './../../../../shared/libraries/utils/date'
import Loader from '../Codes/components/CommonComponents/Loader'
import getManagePaidAmountsCampaignData from './gql'

const formatCurrencyOptions = {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
}

const ManagePaidAmounts = props => {
  const { campaignId } = props.match.params
  const [items, setItems] = useState([])
  const [cities, setCities] = useState([])
  const [syncItems, setSyncItems] = useState({})
  const [currencySign, setCurrencySign] = useState('$')
  const [isLoading, setIsLoading] = useState(true)

  const { data, isLoaded, refetch } = useGraphQLFetch({
    fetchFn: getManagePaidAmountsCampaignData,
    defaultFetchArgs: { campaignId },
    mapResponse: ({ campaign }) => {
      const cityPlans = campaign.cityPlans.nodes
      let items = {}
      let cities = {}
      mockRosters(
        cityPlans.reduce((rosters, cityPlan) => {
          cityPlan.missions.edges.forEach(({ node }) => {
            rosters.push(node)
          })
          return rosters
        }, []),
      )
        .sort(
          (a, b) => b.year + padZero(b.weekNum) - (a.year + padZero(a.weekNum)),
        )
        .forEach(({ weekNum: week, year }) => {
          const startDate = formatToLocalDate(getDateOfISOWeek(week, year))
          cityPlans.forEach(({ city, id: cityPlanId }) => {
            cities = {
              ...cities,
              [city.name]: {
                cost: 0,
                startDate,
                cityPlanId,
                id: city.id,
              },
            }
          })
          items[`${week}-${year}`] = {
            week,
            year,
            ...cities,
          }
        })

      cityPlans.forEach(({ permitCosts, city, id: cityPlanId }) => {
        permitCosts.nodes.forEach(({ startDate, cost }) => {
          const permitDate = dropTimeZoneFromDate(startDate)
          const { weekNumber: week, year } = getWeekNumberAndYear(
            new Date(permitDate),
          )
          if (items[`${week}-${year}`]) {
            items[`${week}-${year}`] = {
              ...items[`${week}-${year}`],
              [city.name]: {
                cost: cost / 100,
                startDate: permitDate,
                cityPlanId,
                id: city.id,
              },
            }
          }
        })
      })

      return {
        currencySign: campaign.country.currencySign,
        items: Object.values(items),
        cities: Object.keys(cities),
      }
    },
  })

  useEffect(() => {
    if (isLoaded) {
      setCities(data.cities)
      setItems(data.items)
      setCurrencySign(data.currencySign)
      setIsLoading(false)
    }
  }, [isLoaded])

  const handleSubmit = () => {
    if (Object.keys(syncItems).length > 0) {
      const body = Object.keys(syncItems).map(key => {
        const item = syncItems[key]
        return {
          ...item,
          cost: Math.floor(Number(item.amount) * 100),
        }
      })
      setIsLoading(true)
      const paymentEdited = async () => {
        await apiClient.editPaymentsByCampaignId(campaignId, body)
        setSyncItems(() => ({}))
        refetch({ campaignId })
      }
      paymentEdited()
    }
  }

  /**
   * Render table with city plans list
   *
   * @returns {*}
   */
  const renderTable = () => {
    const rowsJSX = []
    const citiesSum = []
    let sumTotal = 0

    const headJSX = cities.map((city, index) => {
      const sum = items.reduce((sum, item) => {
        if (item[city]) sum += item[city].cost
        return sum
      }, 0)

      citiesSum.push(
        <Td className="sum-td" key={`bottom-sum-${city}`}>
          {currencyFormatter(sum, formatCurrencyOptions, currencySign)}
        </Td>,
      )
      sumTotal += sum

      return <Th key={`${city}-${index}`}>{city}</Th>
    })
    citiesSum.push(
      <Td className="sum-td" key="bottom-sum-total">
        {currencyFormatter(sumTotal, formatCurrencyOptions, currencySign)}
      </Td>,
    )

    items.forEach((item, index) => {
      rowsJSX.push(renderTableRow(item, index))
    })

    rowsJSX.push(
      <Tr key="bottom-sum">
        <Td className="fixed sum-header">
          SUM <small>(permit costs)</small>
        </Td>
        {citiesSum}
      </Tr>,
    )

    return (
      <div className="table-wrapper">
        <div className="table-scroller">
          <Table className="with-fixed">
            <thead>
              <Tr>
                <Th className="fixed" id={'week'}>
                  Week
                </Th>
                {headJSX}
                <Th className="sum-header">
                  SUM <small>(permit costs)</small>
                </Th>
              </Tr>
            </thead>
            <tbody>{rowsJSX}</tbody>
          </Table>
        </div>
      </div>
    )
  }

  /**
   * Render a row of table
   *
   * @param item
   * @returns {*}
   */
  const renderTableRow = (item, rowIndex) => {
    let sum = 0
    const values = cities.map((city, index) => {
      if (item[city]) {
        sum += item[city].cost
        return (
          <Td key={`${item.week}-${item.year}-${city}`}>
            <RenderInput
              item={item}
              items={items}
              city={city}
              rowIndex={rowIndex}
              cellIndex={index}
              currencySign={currencySign}
              syncItems={syncItems}
              setSyncItems={setSyncItems}
            />
          </Td>
        )
      } else {
        return <Td key={`${item.week}-${item.year}-${index}`} />
      }
    })
    return (
      <Tr key={`${item.week}-${item.year}`}>
        <Td className="fixed">
          {item.week} / {item.year}
        </Td>
        {values}
        <Td className="sum-td">
          {currencyFormatter(sum, formatCurrencyOptions, currencySign)}
        </Td>
      </Tr>
    )
  }

  return (
    <CampaignPage
      active={props.nav}
      campaignId={campaignId}
      match={props.match}
      history={props.history}
    >
      <PageWrapper>
        <LayoutDashboard>
          <h3 style={{ paddingTop: 30, marginTop: 0 }}>Client Permit Costs</h3>
        </LayoutDashboard>
        <form>
          {renderTable()}
          <div className="submit-button">
            <SubmitButton value="Save" onClick={handleSubmit} />
          </div>
        </form>
        {isLoading && <Loader />}
      </PageWrapper>
    </CampaignPage>
  )
}

export default ManagePaidAmounts

/**
 * Render input
 *
 * @param item
 * @returns {*}
 */

const RenderInput = ({
  item,
  items,
  city,
  rowIndex,
  cellIndex,
  currencySign,
  syncItems,
  setSyncItems,
}) => {
  const [value, setValue] = useState(item[city].amount)
  const onChange = ({ target }) => {
    const { value } = target
    if (!target) return
    setValue(value.replace(/[^0-9.]/g, ''))
  }
  const onBlur = ({ target }) => {
    const roster = item[city]
    const { cityPlanId, startDate } = roster
    if (target) {
      items[rowIndex][city] = {
        ...roster,
        amount: target.value,
      }
      if (target.value) {
        setSyncItems(prevState => ({
          ...prevState,
          [`${cityPlanId}${startDate}`]: {
            ...roster,
            amount: target.value,
          },
        }))
      }
    }
  }

  useEffect(() => {
    if (Object.keys(syncItems).length === 0) {
      setValue('')
    }
  }, [syncItems])

  return (
    <div className="cell-input-wrapper">
      <input
        className="cell-input"
        tabIndex={`${rowIndex}${cellIndex + 1}`}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      <span className="cost">
        {currencyFormatter(
          item[city].cost,
          formatCurrencyOptions,
          currencySign,
        )}
      </span>
    </div>
  )
}
